import { FunctionComponent } from 'react'
import { useLocation } from 'react-router'
import { Box, Button, Link, Typography } from '@mui/material'
import { Translation, useIsMobile, usePlatformConfig } from '@dxp-module/common'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import clsx from 'clsx'
import ROUTES from '../../enum/Routes'
import mainHeaderStyles from './mainHeader.styles'
import TradeInBanner from './tradeInBanner/TradeInBanner'

type MainHeaderProps = {
  headerTitle?: string
  openDrawer?: (open: boolean) => void
  backLinkText?: string
  backClickHandler?: () => void
  hideBackCtaOnDesktop?: boolean
  showBannerBackLink: boolean
}

const TitleTranslationMap: {
  [key: string]: string
} = {
  [ROUTES.MYACCOUNT_BASE]: 'trans__myaccount__headers__main_label',
  [ROUTES.MYACCOUNT_ORDERS]: 'trans__myaccount__headers__orders',
  [ROUTES.MYACCOUNT_RESERVATIONS]: 'trans__myaccount__headers__reservation',
  [ROUTES.MYACCOUNT_SAVED_CARS]: 'trans__myaccount__headers__saved_cars',
  [ROUTES.MYACCOUNT_ENQUIRY]: 'trans__myaccount__headers__enquiry',
  [ROUTES.MYACCOUNT_PERSONAL_DETAILS]:
    'trans__myaccount__personal__details__main_label',
  [ROUTES.MYACCOUNT_CHANGE_PASSWORD]:
    'trans__myaccount__change__password__main_label',
  [ROUTES.MYACCOUNT_FINANCE_PERSONAL_DETAILS]: 'trans__myaccount__fapp__header',
  [ROUTES.MYACCOUNT_FINANCE_REDIRECTION]: 'trans__myaccount__fapp__header',
  [ROUTES.MYACCOUNT_FINANCE_CREDIT_OPTIONS]: 'trans__myaccount__fapp__header',
  [ROUTES.MYACCOUNT_FINANCE_FORM]: 'trans__myaccount__fapp__header',
  [ROUTES.MYACCOUNT_FINANCE_CONFIRMATION]: 'trans__myaccount__fapp__header',
  [ROUTES.MYACCOUNT_FINANCE_EXTERNAL_FINANCE_APPLICATION]:
    'trans__myaccount__fapp__header',
  [ROUTES.MYACCOUNT_INSURANCE]: 'trans__myaccount__insapp__header',
  [ROUTES.MYACCOUNT_LOGIN]: 'trans__myaccount__login__header',
  [ROUTES.MYACCOUNT_SET_PASSWORD]:
    'trans__myaccount__create_an_account__header',
  [ROUTES.MYACCOUNT_SERVICEBOOKING_REDIRECTION]:
    'trans__myaccount__menu__main_servicebooking__cta_label', //Change to the correct header name
}

const MainHeader: FunctionComponent<MainHeaderProps> = ({
  headerTitle,
  openDrawer,
  backLinkText,
  hideBackCtaOnDesktop,
  backClickHandler,
  showBannerBackLink,
}) => {
  const location = useLocation()
  const pageTitleTranslationId =
    TitleTranslationMap[location.pathname] || headerTitle
  const {
    platformConfig: { myAccount },
  } = usePlatformConfig()
  const { showMenuButton, displayTradeInBanner, showBannerBackLinkAlways } =
    myAccount || {}
  const isMobile = useIsMobile()
  const styles = mainHeaderStyles()

  return (
    <>
      <Box data-test="myAccount:header" className={styles.header}>
        <Box className={styles.headerContainer}>
          {openDrawer && (
            <>
              {showMenuButton ? (
                <Button
                  variant="outlined"
                  className={styles.menuButton}
                  onClick={() => openDrawer(true)}
                  data-test="myAccount:header:menuButton"
                >
                  <Typography
                    className={styles.menuButtonText}
                    data-test="myAccount:header:MenuButtonText"
                    variant="caption"
                  >
                    <Translation
                      id="trans__myaccount__menu__mobile_menu_CTA_label"
                      component="Typography"
                      componentProps={{ variant: 'button' }}
                    />
                  </Typography>
                </Button>
              ) : (
                <MenuIcon
                  className={styles.headerMenuIcon}
                  onClick={() => openDrawer(true)}
                  data-test="myAccount:header:menuIcon"
                />
              )}
            </>
          )}
          {showBannerBackLink && (
            <Link onClick={backClickHandler}>
              <Typography
                variant="caption"
                data-test="myAccount:header:backLinkText"
                className={clsx(styles.backLinkText, {
                  [styles.hideBackLink]:
                    hideBackCtaOnDesktop && !showBannerBackLinkAlways,
                })}
              >
                <ChevronLeftIcon className={styles.backLinkIcon} />
                <Translation id={backLinkText} />
              </Typography>
            </Link>
          )}
          {pageTitleTranslationId && (
            <Translation
              component="Typography"
              componentProps={{ variant: isMobile ? 'h4' : 'h2' }}
              id={pageTitleTranslationId}
              data-test="myAccount:header:headerTitle"
              className={styles.headerTitle}
            />
          )}
        </Box>
      </Box>
      {displayTradeInBanner && <TradeInBanner />}
    </>
  )
}

export default MainHeader
